/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable complexity */
import React, {
  useContext, useEffect, useMemo, useState,
} from 'react'
import {
  Box,
  Button,
  useResponsiveWrapperContext,
  CarIcon,
  BuildIcon,
  CheckIcon,
  CloseIcon,
  SearchGlassIcon,
  HelpIcon,
} from '@usap/uicomponents'
import RequireVehicleInfoWmo from '@usap/carparts/src/shared/components/ShoppingCartV2/components/Items/Item/VehicleFitment/RequireVehicleInfo/RequireVehicleInfoWmo'
import { usePowersports } from '@usap/carparts/src/shared/hooks/usePowersports'
import { FeatureFlagCookieContext } from '../../context.proxies'
import { Heading4 } from './heading'
import OverlaySpinner from './OverlaySpinner'
import { Container, Label, VehicleContainer } from './VselectorComponents'
import Avatar from './Avatar'
import VehicleFitmentCheckoutWmo from './VselectorComponents/VehicleFitmentCheckoutWmo'

const vehicleLabels = {
  moreInfo: 'More Info Required to Guarantee Fit',
  seRequiredInfo: 'Submodel and Engine Required to Guarantee Fit',
  vehicleInfo: 'Vehicle Info Required to Guarantee Fit',
  customInfo: 'Customized Item',
  noVehicle: 'No Vehicle Information Provided',
  noFitment: 'Fitment Not guaranteed',
  universal: 'Universal Fit',
  guaranteed: 'Guaranteed to Fit',
  doesNotFit: 'This Part Does Not Fit',
  seeFitmentInfo: 'See Vehicle Fitment to Verify Fit',
}

const styleManager = {
  relative: { position: 'relative' },
  leftPad: { left: 'small' },
  greyBorder: { borderColor: '#666666' },
  greyIcon: { background: '#666666', Icon: BuildIcon, IcoSize: '24px' },
  yellowBorder: { borderColor: '#FF9700' },
  greenBorder: { borderColor: '#669944' },
  greenIcon: { background: '#669944', Icon: CheckIcon, IcoSize: '24px' },
  redBorder: { borderColor: 'red' },
  redIcon: { background: 'red', Icon: CloseIcon, IcoSize: '24px' },
  bottomSpacer: { bottom: 'large' },
  xLargeSize: { size: 'xlarge' },
  calcWidth: { width: 'calc(100% - 34px)' },
  yellowIcon: { background: '#FF9700', Icon: CarIcon, IcoSize: '24px' },
  yellowSearchIcon: {
    background: '#FF9700', Icon: SearchGlassIcon, IcoSize: '36px', viewBoxProp: '0 0 36 36',
  },
  containerProps: { borderColor: '#FF9700', width: '100%' },
  avtarProps: { background: '#FF9700', Icon: CarIcon, IcoSize: '24px' },
  fullWidth: { width: '100%' },
}

export default function VSelector({
  universalFit,
  vehicleFit,
  addVehicleTrigger,
  selectedVehicle,
  redirectSearch,
  isCard,
  getProductDetails,
  getProductDetails: {
    orderItemId, part, hasFitNote, seRequired,
  } = {},
  vehicleDataIncomplete,
  GTM,
  cart,
  checkout,
  checkoutIncompleteVehicle,
  ongoingFitmentCall = false,
  hideBorder = false,
  specialPLAVehicleSelector,
  specialPLAVehicleText,
  productOptionsEnabled = false,
  productOption,
  history,
  searchVehicleTrigger,
  isPdpRp1Enabled,
  dataTestPrefix,
  isWmoEnabled,
  recommendation,
}) {
  const isPowersportsEnabled = usePowersports()
  const { isDesktop } = useResponsiveWrapperContext()

  const { newFitmentFlag } = useContext(FeatureFlagCookieContext)

  const {
    year = '', make = '', model = '', submodel = '', engine = '', vehicleType = '',
  } = selectedVehicle || {}

  const vehicleString = [year, make, model, submodel, engine].filter(Boolean).join(' ')

  const hasVehicle = selectedVehicle && Object.keys(selectedVehicle).length
  const labelMargin = useMemo(
    () => (specialPLAVehicleSelector ? {} : { bottom: 'small' }),
    [specialPLAVehicleSelector],
  )

  const showSERequiredBox = newFitmentFlag && seRequired
  const showFitmentBox = newFitmentFlag && hasFitNote

  const containerProps = useMemo(
    () => ({
      containerMargin: hideBorder
        ? {}
        : {
          vertical: isDesktop ? 'xsmall' : 'medium',
        },
      width: '100%',
      borderColor: '#FF9700',
    }), [hideBorder, isDesktop],
  )

  useEffect(() => {
    if (
      selectedVehicle
      && Object.keys(selectedVehicle).length
      && !universalFit
      && !vehicleFit
      && GTM
      && !isCard
    ) {
      const { fitWarning } = GTM
      fitWarning('This Part Does Not Fit', selectedVehicle)
    }
  }, [])

  const commonProps = useMemo(
    () => ({
      hideBorder,
      isCard,
      ongoingFitmentCall,
    }),
    [hideBorder, isCard, ongoingFitmentCall],
  )

  const fitNotes = useMemo(() => getProductDetails.fitNotes || [], [getProductDetails.fitNotes])
  const [showSpinner, shouldShowSpinner] = useState(false)
  const isPowersport = isPowersportsEnabled && ((vehicleType !== '' &&
                        vehicleType?.toLowerCase() !== 'car/truck/suv') ||
                        typeof vehicleType === 'undefined' || vehicleType === '')

  if (
    getProductDetails
    && (getProductDetails.isVendor || getProductDetails.vendorSku)
  ) {
    return (
      <VehicleContainer
        {...commonProps}
        lastLabelSize="small"
        firstLabelText={vehicleLabels.customInfo}
        lastLabelText={vehicleLabels.customInfo}
        dataTestPrefix={dataTestPrefix}
      />
    )
  }

  if (!hasVehicle && recommendation) {
    const product = getProductDetails
    if (product.productId === '') {
      product.productId = Number(product.id)
    }

    return <RequireVehicleInfoWmo product={product} showSpinner={showSpinner} shouldShowSpinner={shouldShowSpinner} recommendation />
  }

  if (!hasVehicle || checkoutIncompleteVehicle) {
    if (checkout && isWmoEnabled) {
      return <VehicleFitmentCheckoutWmo isNoVehicle />
    }
    return (
      <Container hideBorder={hideBorder} borderColor="#FF9700">
        <Box direction="row" style={styleManager.relative}>
          <OverlaySpinner active={ongoingFitmentCall} />
          <Avatar background="#FF9700" Icon={HelpIcon} IcoSize="24px" />
          <Box
            width="calc(100% - 34px)"
            pad={styleManager.leftPad}
            align="start"
            justify="center"
          >
            <Label>{vehicleLabels.noVehicle}</Label>
            {selectedVehicle && (
              <Label size="small">{vehicleLabels.noFitment}</Label>
            )}
          </Box>
        </Box>
      </Container>
    )
  }

  if ((vehicleDataIncomplete || showSERequiredBox) && !universalFit) {
    let headerLabel = vehicleLabels.vehicleInfo
    if (showSERequiredBox) {
      headerLabel = vehicleLabels.seRequiredInfo
    }
    if (specialPLAVehicleSelector) {
      headerLabel = vehicleLabels.moreInfo
    }
    return (
      <VehicleContainer
        {...commonProps}
        selectedVehicle={selectedVehicle}
        containerProps={styleManager.containerProps}
        avatarProps={styleManager.avtarProps}
        labelContainerWidth={styleManager.fullWidth}
        labelMargin={labelMargin}
        firstLabelText={headerLabel}
        lastLabelText={headerLabel}
        lastLabelContainerProps={styleManager.fullWidth}
        addVehicleTrigger={addVehicleTrigger}
        orderItemId={orderItemId}
        hideVehicleModifyLink={showSERequiredBox && checkout}
        specialPLAVehicleSelector={specialPLAVehicleSelector || showSERequiredBox}
        specialPLAVehicleText={showSERequiredBox ? vehicleString : specialPLAVehicleText}
        searchVehicleTrigger={searchVehicleTrigger}
        dataTestPrefix={dataTestPrefix}
      />
    )
  }

  if (universalFit) {
    const addVehicleTriggerSecondParam = selectedVehicle && selectedVehicle.year ? 'change' : 'add'
    if (recommendation || (checkout && isWmoEnabled)) {
      return (
        <VehicleFitmentCheckoutWmo
          isUniversal
          vehicleLabel={vehicleString}
          vehicleDetails={selectedVehicle}
          isPowersport={isPowersport}
          isRecommendation={recommendation}
        />
      )
    }
    return (
      <VehicleContainer
        {...commonProps}
        selectedVehicle={selectedVehicle}
        vehicleString={vehicleString}
        containerProps={styleManager.greyBorder}
        avatarProps={styleManager.greyIcon}
        firstLabelText={vehicleLabels.universal}
        lastLabelText={vehicleLabels.universal}
        addVehicleTrigger={addVehicleTrigger}
        orderItemId={orderItemId}
        cart={cart}
        checkout={checkout}
        isUniversal
        addVehicleTriggerSecondParam={addVehicleTriggerSecondParam}
        dataTestPrefix={dataTestPrefix}
      />
    )
  }

  if (hasVehicle && !universalFit && showFitmentBox) {
    return (
      <VehicleContainer
        hideBorder={hideBorder}
        ongoingFitmentCall={ongoingFitmentCall}
        selectedVehicle={selectedVehicle}
        vehicleString={vehicleString}
        containerProps={styleManager.yellowBorder}
        avatarProps={styleManager.yellowSearchIcon}
        firstLabelText={vehicleLabels.seeFitmentInfo}
        lastLabelText={vehicleLabels.seeFitmentInfo}
        addVehicleTrigger={addVehicleTrigger}
        orderItemId={orderItemId}
        cart={cart}
        checkout={checkout}
        needFitmentChecking
        isCard={isCard}
        fitNotes={fitNotes}
        history={history}
        part={part}
        dataTestPrefix={dataTestPrefix}
      />
    )
  }

  if (hasVehicle && !universalFit && vehicleFit) {
    if (recommendation || (checkout && isWmoEnabled)) {
      return (
        <VehicleFitmentCheckoutWmo
          isGuaranteed
          vehicleLabel={vehicleString}
          vehicleDetails={selectedVehicle}
          isPowersport={isPowersport}
          isRecommendation={recommendation}
        />
      )
    }
    return (
      <VehicleContainer
        {...commonProps}
        selectedVehicle={selectedVehicle}
        vehicleString={vehicleString}
        containerProps={styleManager.greenBorder}
        avatarProps={styleManager.greenIcon}
        firstLabelText={vehicleLabels.guaranteed}
        lastLabelText={vehicleLabels.guaranteed}
        addVehicleTrigger={addVehicleTrigger}
        orderItemId={orderItemId}
        cart={cart}
        checkout={checkout}
        isUniversal
        addVehicleTriggerSecondParam="change"
        productOption={productOption}
        dataTestId="guaranteed-to-fit-badge"
        dataTestPrefix={dataTestPrefix}
      />
    )
  }

  if (hasVehicle && !universalFit && !vehicleFit) {
    if (recommendation || (checkout && isWmoEnabled)) {
      return (
        <VehicleFitmentCheckoutWmo
          isNoFit
          vehicleLabel={vehicleString}
          vehicleDetails={selectedVehicle}
          isPowersport={isPowersport}
          isRecommendation={recommendation}
        />
      )
    }
    return (
      <VehicleContainer
        {...commonProps}
        selectedVehicle={selectedVehicle}
        vehicleString={vehicleString}
        containerProps={styleManager.redBorder}
        avatarProps={styleManager.redIcon}
        firstLabelText={vehicleLabels.doesNotFit}
        lastLabelText={vehicleLabels.doesNotFit}
        addVehicleTrigger={addVehicleTrigger}
        redirectSearch={redirectSearch}
        orderItemId={orderItemId}
        cart={cart}
        checkout={checkout}
        isPdpRp1Enabled={isPdpRp1Enabled}
        isDoesNotFit
        dataTestPrefix={dataTestPrefix}
      />
    )
  }

  if (productOptionsEnabled && !isCard && !isDesktop && !hasVehicle) {
    return (
      <Box
        id="product-options-select-vehicle"
        align="center"
        width="100%"
        margin={styleManager.bottomSpacer}
        round={styleManager.xLargeSize}
        elevation="large"
      >
        <OverlaySpinner active={ongoingFitmentCall} />
        <Button
          primary
          plain={false}
          fill
          alignSelf="center"
          color="orange"
          focusIndicator={false}
          icon={<Avatar background="orange" Icon={CarIcon} IcoSize="28px" />}
          gap="xsmall"
          label={(
            <Heading4
              color="white"
              title="Check If This Fits Your Car"
              style={styleManager.calcWidth}
            />
          )}
          onClick={() => addVehicleTrigger(orderItemId, 'add')}
        />
      </Box>
    )
  }

  return (
    <VehicleContainer
      {...commonProps}
      addVehicleTrigger={addVehicleTrigger}
      containerProps={containerProps}
      avatarProps={styleManager.yellowIcon}
      labelMargin={labelMargin}
      firstLabelText={
        specialPLAVehicleSelector
          ? vehicleLabels.moreInfo
          : vehicleLabels.vehicleInfo
      }
      lastLabelText={vehicleLabels.vehicleInfo}
      specialPLAVehicleText={specialPLAVehicleText}
      dataTestPrefix={dataTestPrefix}
    />
  )
}
